import ICart from '../models/ICart';
import ICompany from '../models/ICompany';
import FbPixel from './fbq';
import { Ga4ShippingTier, trackPurchase } from './ga4-ecommerce';

/** Transaction ids already reported, so a retry or an effect replay cannot report them twice. */
const REPORTED_KEY = '@BSFOOD:reported_purchases';

/**
 * Order waiting for an online payment to be approved. Kept in sessionStorage because the approval
 * is observed by the PIX modal, after the cart that produced it has already been cleared.
 */
const PENDING_KEY = '@BSFOOD:pending_purchase';

export interface IConfirmedPurchase {
  /** `{companyId}:{orderId}`, stable across retries of the same order. */
  transactionId: string;
  companyId: number;
  /** Snapshot of what was ordered, taken before the cart is cleared. */
  cart: ICart[];
  /** Total charged, which is what the Meta Pixel reports as a single value. */
  total: number;
  shipping?: number;
  discountTotal?: number;
  coupon?: string;
  shippingTier?: Ga4ShippingTier;
  paymentType?: string;
  /**
   * Set once the payment is confirmed. A snapshot that reached no destination — tags still
   * loading, for instance — is kept and retried instead of being silently dropped.
   */
  approvedAt?: number;
}

/**
 * Which channels took the event. "Enqueued" and not "delivered": none of these APIs confirms that
 * the vendor processed anything.
 */
export interface ITrackingEmissionResult {
  ga4Enqueued: boolean;
  gtmEnqueued: boolean;
  metaEnqueued: boolean;
}

const NOTHING_SENT: ITrackingEmissionResult = {
  ga4Enqueued: false,
  gtmEnqueued: false,
  metaEnqueued: false,
};

const readReported = (): string[] => {
  try {
    const stored = sessionStorage.getItem(REPORTED_KEY);

    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
};

export const wasPurchaseReported = (transactionId: string): boolean =>
  readReported().includes(transactionId);

const markReported = (transactionId: string): void => {
  try {
    sessionStorage.setItem(REPORTED_KEY, JSON.stringify([...readReported(), transactionId]));
  } catch {
    // A full or unavailable storage must not stop the order flow; GA4 still deduplicates purchase
    // by transaction_id on its side.
  }
};

export const storePendingPurchase = (purchase: IConfirmedPurchase): void => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(purchase));
  } catch {
    // Nothing to do: without the snapshot the approval simply reports no purchase.
  }
};

export const readPendingPurchase = (companyId: number): IConfirmedPurchase | null => {
  try {
    const stored = sessionStorage.getItem(PENDING_KEY);

    if (!stored) return null;

    const pending = JSON.parse(stored) as IConfirmedPurchase;

    // Never report an order left behind by another tenant sharing the browser session.
    return pending.companyId === companyId ? pending : null;
  } catch {
    return null;
  }
};

export const clearPendingPurchase = (): void => {
  sessionStorage.removeItem(PENDING_KEY);
};

type ApprovalListener = () => void;

const approvalListeners = new Set<ApprovalListener>();

/**
 * Notifies that an order was just paid.
 *
 * The approval is observed by the PIX modal, which closes seconds later, while the sending and its
 * retries live above the routes. This is what connects the two without the modal having to own a
 * timer it cannot keep alive.
 */
export const onPendingPurchaseApproved = (listener: ApprovalListener): (() => void) => {
  approvalListeners.add(listener);

  return () => {
    approvalListeners.delete(listener);
  };
};

/** Flags the snapshot as paid, so a later attempt knows it is safe to report. */
export const markPendingPurchaseApproved = (companyId: number): IConfirmedPurchase | null => {
  const pending = readPendingPurchase(companyId);

  if (!pending) return null;

  const approved: IConfirmedPurchase = { ...pending, approvedAt: pending.approvedAt || Date.now() };

  storePendingPurchase(approved);
  approvalListeners.forEach((listener) => listener());

  return approved;
};

/**
 * Reports a purchase that the API has confirmed, to every destination the tenant configured.
 *
 * This is the only place a purchase is reported. Payment that is approved asynchronously — PIX
 * online — must reach here from the approval, never from the order creation, otherwise revenue
 * would be counted for orders that were never paid.
 */
export const trackConfirmedPurchase = (
  company: ICompany,
  purchase: IConfirmedPurchase,
): ITrackingEmissionResult => {
  if (!purchase?.transactionId || wasPurchaseReported(purchase.transactionId)) return NOTHING_SENT;

  const { ga4Enqueued, gtmEnqueued } = trackPurchase(company, purchase.cart, {
    transactionId: purchase.transactionId,
    shipping: purchase.shipping,
    discountTotal: purchase.discountTotal,
    coupon: purchase.coupon,
    shippingTier: purchase.shippingTier,
    paymentType: purchase.paymentType,
  });

  const metaEnqueued = FbPixel.event.purchase(purchase.total);

  // Marked only when some destination actually took it, so a tenant with no integration at all
  // does not record a purchase that would block the event if one gets configured later.
  if (ga4Enqueued || gtmEnqueued || metaEnqueued) {
    markReported(purchase.transactionId);
  }

  return { ga4Enqueued, gtmEnqueued, metaEnqueued };
};

/**
 * Sends a purchase that was paid but never reached any destination.
 *
 * The snapshot survives a reload, so this closes the gap between the approval arriving and the
 * tracking tags being ready. It only ever fires for an approved order, and it drops the snapshot as
 * soon as the event is out — or as soon as it turns out to have been reported already.
 */
export const reportApprovedPendingPurchase = (company: ICompany): ITrackingEmissionResult => {
  const pending = company?.id ? readPendingPurchase(company.id) : null;

  if (!pending?.approvedAt) return NOTHING_SENT;

  if (wasPurchaseReported(pending.transactionId)) {
    clearPendingPurchase();
    return NOTHING_SENT;
  }

  const result = trackConfirmedPurchase(company, pending);

  if (result.ga4Enqueued || result.gtmEnqueued || result.metaEnqueued) {
    clearPendingPurchase();
  }

  return result;
};
